import { BaseTemplateArgs, TemplateResolver, Translator, translator } from '@solid-primitives/i18n';
import {
  ParentProps,
  children,
  splitProps,
  createRoot,
  Component,
  createMemo,
  JSXElement,
  createSignal,
  createComputed,
  createEffect,
} from 'solid-js';
import { setting } from '@bz/request';
// import { setting } from './stores/setting';

/** 替换环境变量 (unused removed) */
const resolveTemplate: TemplateResolver = (string: string, args?: BaseTemplateArgs) => {
  return args ? string.replace(/__(.+?)__/g, (str, key) => String(args[key])) : string;
};

interface I18nInstance {
  t: Translator<Record<string, string>>;
  Trans: Component<ParentProps<TransProps>>;
}

const i18nInstanceCache = new Map<string, I18nInstance>();

const REGS = {
  ids: /<\d>/g,
  id: /\d+/,
  split: /(<\d>.*?<\/\d>|<br\/>)/,
  tag: /<(\d)>(.*?)<\/\d>/,
};

function createI18n(locales: Record<string, () => Promise<unknown>>) {
  // 暂时没找到好的初始化方法

  function createInstance() {
    return createRoot<I18nInstance>(() => {
      const [dict, setDict] = createSignal<Record<string, string>>(undefined as unknown as any);

      const createFunc = import.meta.env.SSR ? createComputed : createEffect;
      createFunc(() => {
        const lang = setting.lang;
        loadResource(lang, locales).then(setDict);
      });

      const t = translator(dict, resolveTemplate);
      const wrapT: typeof t = (path, args) => {
        return t(path, args) || resolveTemplate(replaceEnv(path), args);
      };

      return {
        t: wrapT,
        /**
         * JSX插值，如果要插值过程中添加变量，必须保证他是字符串，
         * 然后把所有的插件当作属性传递，参考下面的deposit变量
         * 比如:
         *<Trans i18nKey='trans.mainpage.lottery.balance' deposit={10}>
            Insufficient balance, select another currency, or <a class='text-primary' href={`/time/${now()}`}>__deposit__</a>
          </Trans>
        */
        Trans: (props) => {
          const [, params] = splitProps(props, ['options', 'i18nKey', 'children']);

          const translation = () => t(props.i18nKey, params);
          if (typeof props.children === 'string') {
            return translation as unknown as string;
          } else {
            const clds = children(() => props.children);

            return createMemo(() => {
              try {
                const trs = translation();
                // 找出翻译里的标签
                const tags = trs.match(REGS.ids);
                // 找出dom节点
                const els = clds.toArray().filter((it) => it instanceof Element);
                if (!tags || tags.length !== els.length) return trs;
                // 排序标签
                const ids = tags.map((it) => it.match(REGS.id)![0]).sort();
                // 分离text和dom
                const arr = trs.split(REGS.split).filter(Boolean);
                // 替换内容
                return arr.map((it) => {
                  const match = it.match(REGS.tag);
                  if (!match) return it;
                  const el = els[ids.indexOf(match[1])] as Element;
                  el.textContent = match[2];
                  return el;
                });
              } catch {
                console.log(props.i18nKey);
                return clds;
              }
            }) as unknown as JSXElement;
          }
        },
      };
    });
  }
  function getInstance() {
    const instance = i18nInstanceCache.get(setting.lang);
    if (!instance) {
      const instance = createInstance();
      i18nInstanceCache.set(setting.lang, instance);
      return instance;
    }
    return instance;
  }
  return {
    t(...args: any) {
      return getInstance().t.apply(undefined, args);
    },
    Trans(...args: any) {
      return getInstance().Trans.apply(undefined, args);
    },
  } as I18nInstance;
}

// const whiteList = new Map<string, string>();

// function registWhiteList(whiteObj: { [key: string]: string }) {
//   for (const whiteKey in whiteObj) {
//     whiteList.set(whiteKey, whiteObj[whiteKey]);
//   }
// }

async function loadResource(lang: string, locales: Record<string, () => Promise<unknown>>) {
  let res: Record<string, string> = {};
  // TODO: 原则上来说命名空间应该和项目名相同，但是现在这个期间很多国际化没处理好，暂时兼容下
  const key = `${lang}/index.json`;
  const localesArr = Object.entries(locales);
  const match =
    localesArr.find(([k]) => k.endsWith(key)) || localesArr.find(([key]) => key.endsWith(`en-US/index.json`));
  if (!lang || !match) {
    res = {};
  } else {
    res = (await match[1]()) as Record<string, string>;

    for (const i in res) {
      res[i] = replaceEnv(res[i]);

      // 分站文案替换需求
      // whiteList.forEach((whiteValue, whiteKey) => {
      //   if (res[i].includes(whiteKey)) {
      //     const regex = new RegExp(whiteKey, 'g');
      //     res[i] = res[i].replace(regex, whiteValue);
      //   }
      // });
    }
  }
  return res;
}

function replaceEnv(value: string) {
  return value;
  // return value.replace(/__ENV_\w+__/g, (str) => {
  //   return (
  //     env[(I18nENV as any)[str] as keyof typeof env] as string
  //   ).toUpperCase();
  // });
}

/**
 * JSX插值，如果要插值过程中添加变量，必须保证他是字符串，
 * 然后把所有的插件当作属性传递，参考下面的deposit变量
 * 比如:
 *<Trans i18nKey='trans.mainpage.lottery.balance' deposit={10}>
    Insufficient balance, select another currency, or <a class='text-primary' href={`/time/${now()}`}>__deposit__</a>
  </Trans>
*/
type TransProps = ParentProps<{
  i18nKey: string;
  options?: any;
  [key: string]: any;
}>;

export const { t, Trans } = createI18n(import.meta.glob('./locales/**/*.json', { import: 'default' }));
